// eslint-disable-next-line max-classes-per-file
import type { ComposerTranslation } from 'vue-i18n';
import type { Airflow } from '@aviata/platform-types';
import { isNil, sortBy, sortedUniqBy, toSafeInteger } from 'lodash-es';

import { baggageToString } from '@/entities/baggage';
import { getHandLuggageFromOffer } from '@/entities/offer';
import {
  FF_SERVICE_STATE,
  FF_SERVICE_TYPE,
  createFareFeatures,
  mapServicesByState,
  mapServiceStateToTitle,
  getLuggageServiceState,
  getBaggageServiceState,
  type FareFeature,
} from '@/entities/fare';

export interface FareFamilyChoiceSpec {
  get id(): number;

  get code(): string | null;

  get name(): string;

  get price(): Airflow.Price;

  get descriptions(): Airflow.FareFamily.Description.Entity[];

  get features(): FareFeature[];
}

/**
 * @deprecated fare_families_flexible will cover default fares for owc/vi
 */
export class BaseFareFamilyChoice implements FareFamilyChoiceSpec {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private readonly offer: Airflow.Offer.Details['offer'],
    private readonly t: ComposerTranslation,
  ) {}

  // eslint-disable-next-line class-methods-use-this
  get id() {
    return 0;
  }

  // eslint-disable-next-line class-methods-use-this
  get code() {
    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  get name() {
    return this.t('fareFamily.standard');
  }

  get price() {
    // in case we have price_breakdown as for FA offers it has more priority
    return {
      currency: this.offer.price.currency,
      amount: toSafeInteger(
        this.offer.price_breakdown?.total ?? this.offer.price.amount,
      ).toString(),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  get descriptions() {
    return [
      this.handLuggageService(),
      this.baggageService(),
      this.refundService(),
      this.exchangeService(),
    ] as Airflow.FareFamily.Description.Entity[];
  }

  private handLuggageService() {
    const handLuggageList = getHandLuggageFromOffer(this.offer);
    const state = getLuggageServiceState(handLuggageList);

    let value: string | undefined;

    if (state === FF_SERVICE_STATE.INCLUDED) {
      // in most cases all elements will be the same, but if somehow there is different values, we want to take the smallest luggage
      const uniqLuggage = sortedUniqBy(
        sortBy(handLuggageList, 'value'),
        'value',
      );
      if (uniqLuggage[0]) {
        value = baggageToString({ baggage: uniqLuggage[0], t: this.t });
      }
    }

    const name = mapServiceStateToTitle({
      type: FF_SERVICE_TYPE.HAND_LUGGAGE,
      state,
      value,
      t: this.t,
    });

    return {
      type: FF_SERVICE_TYPE.HAND_LUGGAGE,
      state,
      name,
    };
  }

  private baggageService() {
    const rawBaggage = this.offer.features?.baggage || null;
    const state = getBaggageServiceState(rawBaggage);

    let value: string | undefined;

    if (state === FF_SERVICE_STATE.INCLUDED) {
      if (rawBaggage) {
        value = baggageToString({ baggage: rawBaggage, t: this.t });
      }
    }

    const name = mapServiceStateToTitle({
      type: FF_SERVICE_TYPE.BAGGAGE,
      state,
      value,
      t: this.t,
    });

    return {
      type: FF_SERVICE_TYPE.BAGGAGE,
      state,
      name,
    };
  }

  private refundService() {
    const refundFeature = this.offer.features?.refundable?.value;
    let state = isNil(refundFeature)
      ? FF_SERVICE_STATE.UNKNOWN
      : FF_SERVICE_STATE.NOT_OFFER;

    if (refundFeature) {
      state = FF_SERVICE_STATE.INCLUDED;
    }

    return {
      type: FF_SERVICE_TYPE.REFUND,
      state,
      name: mapServiceStateToTitle({
        type: FF_SERVICE_TYPE.REFUND,
        state,
        t: this.t,
      }),
    };
  }

  private exchangeService() {
    const state =
      this.refundService().state === FF_SERVICE_STATE.INCLUDED
        ? FF_SERVICE_STATE.CHARGEABLE
        : FF_SERVICE_STATE.NOT_OFFER;

    return {
      type: FF_SERVICE_TYPE.EXCHANGE,
      state,
      name: mapServiceStateToTitle({
        type: FF_SERVICE_TYPE.EXCHANGE,
        state,
        t: this.t,
      }),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  get features() {
    return [];
  }
}

export class FareFamilyChoice implements FareFamilyChoiceSpec {
  // eslint-disable-next-line no-useless-constructor
  constructor(readonly rawFareChoice: Airflow.FareFamily.Choice) {}

  get id() {
    return this.rawFareChoice.id;
  }

  get code() {
    return this.rawFareChoice.code;
  }

  get name() {
    return this.rawFareChoice.name;
  }

  get price() {
    return {
      ...this.rawFareChoice.price,
      amount: toSafeInteger(
        this.rawFareChoice.price_breakdown?.total ??
          this.rawFareChoice.price.amount,
      ).toString(),
      initial_amount: this.rawFareChoice.discount?.initial_amount,
    };
  }

  get descriptions() {
    return !isNil(this.rawFareChoice.descriptions)
      ? mapServicesByState(this.rawFareChoice.descriptions)
      : [];
  }

  get features() {
    return createFareFeatures(this.rawFareChoice);
  }
}
